import React, { useEffect, useState } from 'react';

import { ChevronDown } from '@gravity-ui/icons';
import { Alert, Button, Icon } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getI18nLang } from '@yandex-int/i18n';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { appendLogs } from 'client/blocks/SmartHomeTestPage/SmartHomeTestPage';
import { AliceAppTestPageHistoryHighlighter } from 'client/blocks-redesign/AliceAppTestPageHistoryHighlighter/AliceAppTestPageHistoryHighlighter';
import { PageLayout } from 'client/blocks-redesign/Layout/PageLayout';
import { useMessageSubscription } from 'client/hooks/useMessageSubscription';
import { getSmartHomeTestingChecklistTexts } from 'client/locale/ru/smartHomeTesting';
import { Skill } from 'client/model/skill';
import { getIsYangoRequest, getQuasarUiUrl } from 'client/utils';
import { openAccountLinkingWindow } from 'client/utils/accountLinking';
import { quasar } from 'client/utils/messages';
import * as keyset from 'i18n/Testing';
import * as testPageKeyset from 'i18n/TestPage';

import styles from './SmartHomeTestPage.module.scss';

const t = i18n(keyset);
const tTestPage = i18n(testPageKeyset);

interface Props {
    skill: Skill;
    isDraft: boolean;
}

export const SmartHomeTestPage: React.FC<Props> = ({ skill, isDraft }) => {
    const [alertIsOpen, setAlertIsOpen] = useState(true);
    const [debugInfo, setDebugInfo] = useState<string[]>([]);

    useMessageSubscription((data) => {
        switch (data.type) {
            case 'dialogs_account-linking_start': {
                const applicationName = encodeURIComponent(data.payload.applicationName);
                const url = `${__appConfig.app.webLinkingUrl}?consumer=passport&application_name=${applicationName}`;

                openAccountLinkingWindow({
                    url,
                    onSuccess: () => quasar.sendAccountLinkingResult('ok'),
                    onFailure: () => quasar.sendAccountLinkingResult('error'),
                });

                return;
            }
            case 'dialogs_debug-info': {
                if (data.payload.debugInfo && data.payload.debugInfo.logs) {
                    setDebugInfo(appendLogs(debugInfo, data.payload.debugInfo.logs));
                } else {
                    console.error('Malformed debug info');
                }

                return;
            }
        }
    });

    useEffect(() => {
        setDebugInfo([]);
    }, [isDraft]);

    const testedSkillId = isDraft ? `${skill.id}-draft` : skill.id;
    const isYangoRequest = getIsYangoRequest();
    const lang = getI18nLang() === 'ru' ? 'ru-RU' : 'en-US';
    const frameSrc = isYangoRequest
        ? `${getQuasarUiUrl()}?lang=${lang}&app_id=com.yasmina.iot&from=dialogs&tested_skill_id=${testedSkillId}`
        : `${getQuasarUiUrl()}?from=dialogs&tested_skill_id=${testedSkillId}`;

    const debugString = debugInfo.join('\n\n');

    return (
        <ErrorBoundary withHomepageLink>
            <PageLayout
                aliceApp={skill}
                pageTitle={`${t('headline')}: ${isDraft ? tTestPage('menu-draft') : tTestPage('menu-published')}`}
            >
                {!isDraft || skill.draft.isAllowedForDeploy ? (
                    <>
                        <Alert
                            title={
                                <span className={styles.smartHomeTestPage__alertTitle}>
                                    {t('check-that-your-skill-is-ready')}
                                </span>
                            }
                            message={
                                alertIsOpen ? (
                                    <ul className={styles.smartHomeTestPage__alertList}>
                                        {getSmartHomeTestingChecklistTexts().map((text, i) => (
                                            <li key={i}>{text}</li>
                                        ))}
                                    </ul>
                                ) : null
                            }
                            onClose={alertIsOpen ? () => setAlertIsOpen(false) : undefined}
                            actions={
                                alertIsOpen ? undefined : (
                                    <Alert.Action onClick={() => setAlertIsOpen(true)} size="s">
                                        <Icon data={ChevronDown} />
                                    </Alert.Action>
                                )
                            }
                            layout={alertIsOpen ? 'vertical' : 'horizontal'}
                            theme="warning"
                        />
                        <section className={styles.smartHomeTestPage__content}>
                            <article>
                                <div className={styles.smartHomeTestPage__headToolbar}>
                                    <h2 className={styles.smartHomeTestPage__title}>{t('smart-home')}</h2>
                                    <Button onClick={quasar.moveToDeviceList}>{t('devices')}</Button>
                                </div>
                                <iframe
                                    className={styles.smartHomeTestPage__iframe}
                                    src={frameSrc}
                                    key={testedSkillId}
                                />
                            </article>
                            <article className={styles.smartHomeTestPage__historyColumn}>
                                <div className={styles.smartHomeTestPage__headToolbar}>
                                    <h2 className={styles.smartHomeTestPage__title}>{t('log')}</h2>
                                </div>
                                <div className={styles.smartHomeTestPage__history}>
                                    <AliceAppTestPageHistoryHighlighter data={debugString} />
                                </div>
                            </article>
                        </section>
                    </>
                ) : (
                    <div>{t('fill-fields-to-test')}</div>
                )}
            </PageLayout>
        </ErrorBoundary>
    );
};
